import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Check, HelpCircle } from 'lucide-react';
import api from '@/lib/api';
import { useAttachmentImages } from '@/hooks/useAttachmentImages';
import { AttachmentImage } from '@/components/AttachmentImage';
import { useFileRequestCategories, labelFor, useFileRequestCopy, copyFor, interpolate }
  from '@/lib/fileRequestCategories';

/**
 * Client-facing file/info request — the "at risk" notice and the two-verdict reply.
 *
 * WORDING RULE — read before changing any string here:
 * The delivery date the client can see HAS NOT MOVED and is still true. So this
 * says delivery is AT RISK pending the files, which is a statement about
 * consequences that follows from their own inaction. It must never say the
 * project is "on hold" or "paused", and must never state a new date: a client
 * reading "ON HOLD" beside an unchanged ETA has been told two contradictory
 * things, will believe the date, and will email asking which is right.
 *
 * The notice QUALIFIES the date rather than competing with it — it renders the
 * estimate and the condition in one sentence, so there is nothing to reconcile.
 *
 * Editor identity never appears: the request is from Uncut Gems. The server
 * strips created_by / created_by_name before this ever reaches the browser
 * (server.redact_for_client), so this component cannot leak it even by mistake.
 */

const NOTE_MAX = 1000;

const fmtDate = (d) => {
  if (!d) return null;
  try {
    return new Date(d + 'T00:00:00').toLocaleDateString('en-US',
      { month: 'long', day: 'numeric' });
  } catch { return null; }
};

export function ClientFileRequest({ project, onRefresh, slot = 'banner' }) {
  const req = (project.file_requests || []).find(r => r.status === 'open');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [choice, setChoice] = useState(null);   // which verdict is being answered
  const { imageState, retryImage, reportLoaded, reportError } =
    useAttachmentImages(req?.images, { transport: 'blob' });
  const categories = useFileRequestCategories();
  const copy = useFileRequestCopy();
  // Every sentence below is keyed on (render, category). `cat` is the whole of
  // the category logic in this file — there is no frame to drop a noun into.
  const cat = req?.category || '';
  const t = (key) => copyFor(copy, key, cat);

  /* THE CLOSED NOTICE — session-only, for the person watching.
   *
   * When a request closes, the panel has nothing to render: it selects on
   * `status === 'open'` and there is no longer an open one. Someone sitting on the
   * page watches the ask evaporate with no explanation — the original symptom, and
   * the one thing the closed.client email cannot fix, because it arrives in their
   * inbox rather than on the screen they are looking at.
   *
   * DELIBERATELY NOT A SECOND PREDICATE. The client surface hangs off exactly one
   * derived question (`awaiting_client`) and that is worth keeping. This is a
   * component-memory observation — "the thing I was rendering is gone" — not new
   * server state. It is lost on refresh, which is acceptable: it exists for the
   * person actually watching, and the email covers everyone else.
   *
   * THE SUPERSEDE CASE IS WHY THIS KEYS ON ABSENCE, NOT ON THE NUMBER CHANGING.
   * Replacing a request closes the old one and creates the new one in a single
   * atomic write, so a "the number changed" test would flash "no longer needed" a
   * beat before the new ask appeared. During a supersede there is always an open
   * request, so keying on `req` being absent means the notice never arms. */
  const [closedNotice, setClosedNotice] = useState(null);
  const lastOpen = useRef(null);
  const selfActed = useRef(false);    // the client closed it themselves — see below
  useEffect(() => {
    if (req) {
      lastOpen.current = { category: req.category, wasAnswered: !!(req.responses || []).length };
      setClosedNotice(null);          // an open request always outranks a stale notice
    } else if (lastOpen.current) {
      // NOT AFTER THEIR OWN ACTION. Answering "they're uploaded" resolves the
      // request, so the panel empties — but they just pressed a button and already
      // got a toast. Telling them it is no longer needed restates their own action
      // back at them. Same exclusion the closed.client email makes for
      // `client_response`.
      if (!selfActed.current) setClosedNotice(lastOpen.current);
      selfActed.current = false;
      lastOpen.current = null;
    }
  }, [req]);

  if (!req) {
    // Only the slot the closed request was living in shows the notice.
    const slotMatches = closedNotice
      && (closedNotice.wasAnswered ? slot === 'files' : slot === 'banner');
    if (!closedNotice || !slotMatches) return null;
    const ct = (key) => copyFor(copy, key, closedNotice.category || '');
    return (
      <div className="rounded-xl bg-ug-raised border border-ug-border p-5"
           data-testid="client-file-request-closed">
        <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500 block"
              data-testid="client-file-request-closed-title">
          {ct('client_panel.closed_title')}
        </span>
        <p className="font-sans text-[12px] text-zinc-200 mt-1.5 leading-relaxed"
           data-testid="client-file-request-closed-body">
          {ct('client_panel.closed_body')}
        </p>
        <button onClick={() => setClosedNotice(null)}
                data-testid="client-file-request-closed-dismiss"
                className="tap-auto mt-3 rounded-md border border-ug-border px-3 py-1.5 font-sans text-[11px] font-semibold text-zinc-100/80 hover:border-ug-gold/50 transition-colors">
          {ct('client_panel.closed_dismiss')}
        </button>
      </div>
    );
  }

  const eta = fmtDate(project.eta);
  // THREE STATES, all derived from the request itself — nothing stored:
  //   A  !answered                     open, waiting on them   -> slab + at-risk + verdicts
  //   B  answered && cannotProvide     open, they have replied  -> plain panel + revise
  //   C  resolved                      not sent to the client at all -> renders null
  // `files_uploaded` sets the status to resolved server-side, so an answered
  // request that reaches this component is always the cannot_provide case; the
  // flag is kept explicit rather than assumed so state B cannot silently widen.
  const answered = (req.responses || []).slice(-1)[0];
  const cannotProvide = answered?.verdict === 'cannot_provide';

  /* WHERE EACH STATE RENDERS — the two states have different jobs, so they do not
   * belong in the same place.
   *
   *   slot="banner"  above the tab strip   STATE A only — an unanswered ask. It
   *                                        earns that position: a client arriving
   *                                        from the request email must land on it
   *                                        without first choosing a tab.
   *   slot="files"   inside the Files tab  STATE B only — already answered. This is
   *                                        a RECORD, not an ask, and nobody is
   *                                        waiting on the client any more.
   *
   * State B used to sit above the tabs too, and it had no expiry: `cannot_provide`
   * deliberately leaves the request open, and only an editor or admin closing it
   * ever removes the panel. So a client who answered weeks ago and came back to
   * review their first draft still met a box above the tabs about footage nobody
   * was waiting on — competing with the draft we actually want them looking at.
   *
   * The request's STATUS is untouched by this. It stays open for the editor and for
   * admin exactly as before; this only decides where the client sees it. */
  if (slot === 'banner' && answered) return null;
  if (slot === 'files' && !answered) return null;

  const respond = async (verdict) => {
    // The location IS the value of the "uploaded" verdict — see the copy entry
    // and the server's matching 400. Gate BOTH callers (state A verdict, state B
    // revise) here so neither can resolve the request with nothing to act on.
    if (verdict === 'files_uploaded' && !note.trim()) {
      toast.error(t('client_panel.note_required_uploaded'));
      return;
    }
    selfActed.current = true;   // suppress the closed notice for our own action
    setSaving(true);
    try {
      await api.put(`/projects/${project.id}/file-requests/${req.request_number}/respond`,
                    { verdict, note: note.trim() });
      toast.success(verdict === 'files_uploaded'
        ? 'Thanks — we’ve let the team know'
        : 'Thanks — we’ll take it from here');
      setNote(''); setChoice(null);
      if (onRefresh) onRefresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'object' && d?.message ? d.message : 'Could not send your reply');
      if (onRefresh) onRefresh();
    } finally { setSaving(false); }
  };

  return (
    /* SLAB HEADER + PLAIN BODY.
     *
     * This was a white card with a hairline gold border, which is the exact
     * flatness the light-mode work fixed elsewhere: on paper a 1px border at
     * 30% alpha is not an edge, so the panel dissolved into the drawer and the
     * one thing on the page that needs the client's attention was the quietest
     * thing on it. Dark mode hid the problem — the same border reads fine
     * against dark chrome.
     *
     * `bg-ug-gold text-ug-deepest` on the slab is the app's primary-action pair
     * and is theme-aware for free: index.css "PRIMARY ACTION: dark, not gold"
     * remaps `.light .bg-ug-gold` to a #1c1f25 slab with #f2efe9 ink, so this
     * renders gold-on-dark in dark mode and dark-on-paper in light. Same pair
     * as the dashboard's "Files requested" chip, deliberately — the chip in the
     * list and this slab are meant to be recognisably one thing.
     *
     * Do NOT substitute `bg-gold-leaf`/`text-obsidian`. They look equivalent and
     * have more precedents, but neither alias has a `.light` rule, so the slab
     * would stay #c6a96f on paper at 1.9:1 against it.
     *
     * NO tint on the body. See the note above the body div.
     */
    <div className={`rounded-xl bg-ug-raised overflow-hidden border ${
                      answered ? 'border-ug-border' : 'border-ug-gold/40'}`}
         data-testid="client-file-request"
         data-request-state={answered ? 'answered' : 'awaiting'}>
      {/* THE SLAB IS STATE A ONLY. It says "we need something from you", so it
          must disappear the moment they have given it to us — an unanswered ask
          and an answered one are not the same message, and leaving the loud
          treatment on after they have acted tells them to act again when there is
          nothing to do. That is the same class of error as showing "ON HOLD"
          beside an unchanged delivery date: the client reads two contradictory
          things and writes in to ask which is true.

          State B is not a new visual language, it is the ABSENCE of this one —
          the plain raised panel with a neutral border that every settled thing in
          the app already uses.

          The icon and eyebrow live INSIDE the slab, so both must read against it.
          The icon is `text-ug-deepest` for dark mode (dark glyph on gold); in
          light mode index.css's `.light .bg-ug-gold *` forces every child to
          #f2efe9, which carries the icon with it because lucide strokes with
          currentColor. Left as `text-ug-gold` it would have been gold-on-gold in
          dark and near-invisible dark-on-dark in light. */}
      {!answered && (
        <div className="flex items-center gap-2 bg-ug-gold px-4 py-2.5"
             data-testid="client-file-request-slab">
          <AlertTriangle className="w-4 h-4 shrink-0 text-ug-deepest" />
          <span className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-deepest">
            {t('client_panel.slab')}
          </span>
        </div>
      )}

      {/* Body is the plain raised surface, NOT a low-alpha gold wash. The slab
          is already carrying the emphasis; a tint underneath competes with it
          and, worse, is asymmetric across themes — index.css flattens every
          `bg-ug-gold/*` to a single 4% on paper, so the tint that reads in dark
          mode is almost absent in light. Tried both; the untinted body let the
          slab stay the loudest thing in the panel in BOTH themes. */}
      <div className="p-5">
          {/* STATE A ONLY. The at-risk line is a statement about what happens if we
              DON'T get something — it cannot survive the client telling us they
              haven't got it. Leaving it up would keep a warning on screen whose
              only remedy the client has already ruled out. */}
          {!answered && (
            /* The date and the condition in ONE sentence, so there is nothing for
               the client to reconcile against the estimate shown elsewhere. */
            <p className="font-sans text-[12px] text-zinc-200 leading-relaxed"
               data-testid="client-file-request-at-risk">
              {interpolate(
                t(eta ? 'client_panel.at_risk' : 'client_panel.at_risk_no_eta'),
                { eta: <span className="font-semibold">{eta}</span>,
                  at_risk: <span className="font-semibold text-ug-gold">at risk</span> })}
            </p>
          )}

          {/* STATE B opener. Says what they did and what we are doing, in that
              order, with no ask in it. */}
          {answered && (
            <p className="font-sans text-[12px] text-zinc-200 leading-relaxed"
               data-testid="client-file-request-answered">
              {t('client_panel.answered_opener')}
            </p>
          )}

          {/* Shown in BOTH states, and in state B it is load-bearing rather than
              decorative: "you don't have these" needs a referent, and a client
              coming back days later will not remember which ask this was. */}
          <div className="mt-3 rounded-md bg-ug-surface border border-ug-border px-3 py-2.5"
               data-testid="client-file-request-body">
            <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-zinc-500 block">
              {labelFor(categories, req.category)}
            </span>
            <p className="font-sans text-[12px] text-zinc-200 mt-1.5 leading-relaxed">{req.note}</p>
            {(req.images || []).length > 0 && (
              <div className="mt-2.5 space-y-2" data-testid="client-file-request-images">
                {/* Never conditional on success. A screenshot that cannot be loaded
                    still renders — as a named chip — because this client was emailed
                    "1 screenshot attached" and empty space reads as "there isn't one". */}
                {(req.images || []).map((img, i) => (
                  <AttachmentImage key={img.file_id}
                                   state={imageState[img.file_id]}
                                   filename={img.filename}
                                   onRetry={() => retryImage(img.file_id)}
                                onLoaded={() => reportLoaded(img.file_id)}
                                onLoadError={() => reportError(img.file_id)}
                                   testId={`client-file-request-image-${i}`} />
                ))}
              </div>
            )}
          </div>

          {answered ? (
            <>
              {/* Their own words back to them. A client who half-remembers
                  answering needs to see WHAT they said, not just that they did. */}
              {answered.note && (
                <div className="mt-3 rounded-md border border-ug-border px-3 py-2.5"
                     data-testid="client-file-request-your-reply">
                  <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-zinc-500 block">
                    {t('client_panel.your_reply_label')}
                  </span>
                  <p className="font-sans text-[12px] text-zinc-200 mt-1.5 leading-relaxed">{answered.note}</p>
                </div>
              )}

              {/* FIX 3 — the client may revise while the request is still open.
                  The API was built for exactly this ("I don't have these" then
                  "found them, they're up now" — see the respond endpoint), and the
                  UI was the only thing preventing it: the panel used to swap the
                  buttons for a thank-you the moment ANY response existed, so a
                  client who located the files afterwards had no way to say so and
                  the editor kept waiting on a request the client had already
                  overtaken.

                  ONE button, not two. Re-asserting "I don't have these" is a no-op
                  the server already treats as a duplicate, and offering it back
                  would read as asking them to answer the same question twice. */}
              {cannotProvide && (
                <div className="mt-3.5" data-testid="client-file-request-revise">
                  <label className="font-sans text-[9px] font-semibold uppercase tracking-wider text-zinc-500 block mb-1.5">
                    {t('client_panel.revise_label')}
                  </label>
                  <textarea value={note} onChange={e => setNote(e.target.value.slice(0, NOTE_MAX))}
                            data-testid="client-file-request-revise-note"
                            rows={2}
                            placeholder={t('client_panel.revise_placeholder')}
                            className="w-full rounded-md bg-ug-surface border border-ug-border px-2.5 py-2 font-sans text-[12px] text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-ug-gold/50 resize-none" />
                  <button onClick={() => { setChoice('files_uploaded'); respond('files_uploaded'); }}
                          disabled={saving}
                          data-testid="client-verdict-files-uploaded-revised"
                          className="tap-auto mt-2.5 w-full flex items-center justify-center gap-1.5 rounded-md bg-ug-surface border border-ug-border px-3 py-2 font-sans text-[11px] font-semibold text-zinc-200 hover:border-ug-gold/50 transition-colors disabled:opacity-50">
                    <Check className="w-3.5 h-3.5" /> {t('client_panel.revise_button')}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="mt-3.5">
              <label className="font-sans text-[9px] font-semibold uppercase tracking-wider text-zinc-500 block mb-1.5">
                {t('client_panel.note_label')}
              </label>
              <textarea value={note} onChange={e => setNote(e.target.value.slice(0, NOTE_MAX))}
                        data-testid="client-file-request-note"
                        rows={2}
                        placeholder={t('client_panel.note_placeholder')}
                        className="w-full rounded-md bg-ug-surface border border-ug-border px-2.5 py-2 font-sans text-[12px] text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-ug-gold/50 resize-none" />

              {/* BOTH verdicts get the same weight — same size, same padding, same
                  row. "I don't have these" is the signal that tells us to step in,
                  so if it reads as admitting failure the client avoids it and we
                  lose exactly the case a human is needed for. It is styled as a
                  neutral choice, never as a lesser or destructive one. */}
              <div className="flex items-center gap-2 mt-2.5" data-testid="client-file-request-verdicts">
                <button onClick={() => { setChoice('files_uploaded'); respond('files_uploaded'); }}
                        disabled={saving}
                        data-testid="client-verdict-files-uploaded"
                        className="tap-auto flex-1 flex items-center justify-center gap-1.5 rounded-md bg-ug-surface border border-ug-border px-3 py-2 font-sans text-[11px] font-semibold text-zinc-200 hover:border-ug-gold/50 transition-colors disabled:opacity-50">
                  <Check className="w-3.5 h-3.5" /> {t('client_panel.verdict_affirmative')}
                </button>
                <button onClick={() => { setChoice('cannot_provide'); respond('cannot_provide'); }}
                        disabled={saving}
                        data-testid="client-verdict-cannot-provide"
                        className="tap-auto flex-1 flex items-center justify-center gap-1.5 rounded-md bg-ug-surface border border-ug-border px-3 py-2 font-sans text-[11px] font-semibold text-zinc-200 hover:border-ug-gold/50 transition-colors disabled:opacity-50">
                  <HelpCircle className="w-3.5 h-3.5" /> {t('client_panel.verdict_negative')}
                </button>
              </div>
              {/* Applies to BOTH buttons on purpose. Reassurance attached only to
                  "I don't have these" marks it as the answer needing an excuse,
                  which quietly confirms it is the lesser one — the opposite of the
                  goal. A neutral line about what happens next carries no such
                  signal, and it is true: cannot_provide escalates to admin
                  immediately rather than waiting on the 48h sweep.
                  (An earlier draft said we would help them find their files. We
                  cannot — they are on the client's own drive. Do not reinstate.) */}
              <p className="font-sans text-[10px] text-zinc-500 mt-2">
                {t('client_panel.either_answer')}
              </p>
            </div>
          )}
      </div>
    </div>
  );
}

export default ClientFileRequest;
